import { atom, useAtom } from "jotai";
import { useEffect } from "react";

/**
 * Starts false everywhere and only becomes true in the effect below, after
 * hydration.
 *
 * It must not be seeded from storage: the server always renders `false`, and
 * React does not patch an attribute (an img `src`, an icon `color`) that
 * differs from the server's markup when it hydrates. A persisted `true` made
 * the client's first render disagree with the server's, and dark-mode users
 * were left with light icons. Starting false keeps hydration in step, and the
 * effect's update is an ordinary re-render, which React does apply.
 * `html.dark` itself is set before first paint by the script in `__root.tsx`.
 */
const darkModeAtom = atom(false);

export function useDarkMode() {
  const [isDarkMode, setIsDarkMode] = useAtom(darkModeAtom);

  useEffect(() => {
    const darkMode = window.matchMedia("(prefers-color-scheme: dark)");

    setIsDarkMode(darkMode.matches);
    if (darkMode.matches) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }

    const handler = (event: MediaQueryListEvent) => {
      setIsDarkMode(event.matches);
      if (event.matches) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    };

    darkMode.addEventListener("change", handler);

    return () => {
      darkMode.removeEventListener("change", handler);
    };
  }, [setIsDarkMode]);

  return isDarkMode;
}
